import {
  cookieStorage,
  createStorage,
  type CreateConnectorFn,
} from "@wagmi/core";
import { http, fallback, Transport } from 'viem'; // Added fallback
import {
  baseAccount,
  injected,
  metaMask,
  safe,
} from "wagmi/connectors";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { createRateLimitedTransport } from "../util/rate-limited-transport";

import {
  metaDescription,
  logoExt,
  metaTitle,
  baseUrl,
} from "../constants/project-specific/project";
import { shibarium, dogechain, base, type AppKitNetwork, mainnet } from "@reown/appkit/networks";
import { chainSettings, ChainType, defaultRPCSettings } from "@/constants/chain-specific";

// Define the interface for Chain RPC Configuration
interface ChainRpcConfig {
  chainId: number;
  primaryRpcUrl: string;
  fallbackRpcUrls: string[];
}

// Get projectId from https://cloud.walletconnect.com
export const projectId = process.env.NEXT_PUBLIC_PROJECT_ID ?? "";

export const ipfsGateways: string[] = process.env.NEXT_PUBLIC_IPFS_GATEWAYS
  ? process.env.NEXT_PUBLIC_IPFS_GATEWAYS.split(",")
  : "https://outrageous-amethyst-beetle.myfilebase.com/ipfs/,https://ipfs.io/ipfs/,https://dweb.link/ipfs/,https://nftstorage.link/ipfs/".split(",");

export const defaultIpfsGateway = ipfsGateways[0];

// Skip configuration during build/SSG phase
const isBuildTime = typeof window === 'undefined' && process.env.NODE_ENV !== 'development';

if (isBuildTime) {
  console.log('⚠️ Skipping Wagmi configuration during build phase');
}

// chains to be used in the app
export const appChains: [AppKitNetwork, ...AppKitNetwork[]] = [shibarium, dogechain, base, mainnet];

// RPC Configuration Store:
// This object, `chainRpcConfigs`, stores the RPC configuration for each chain supported by the application.
// It maps a chainId (number) to a `ChainRpcConfig` object, which includes:
// - `chainId`: The numerical ID of the chain.
// - `primaryRpcUrl`: The primary RPC URL for this chain.
// - `fallbackRpcUrls`: An array of fallback RPC URLs to be used if the primary fails.
//
// Population Logic:
// For each chain in `appChains`:
// 1. It constructs environment variable names based on the chain's name (e.g., "mainnet" becomes "MAINNET").
//    - Primary RPC: `NEXT_PUBLIC_MAINNET_PRIMARY_RPC`
//    - Fallback RPCs: `NEXT_PUBLIC_MAINNET_FALLBACK_RPCS` (comma-separated string)
// 2. It reads these environment variables using `process.env`.
// 3. If a primary RPC URL is not defined in the environment variables, it attempts to use a default public RPC
//    URL specified in the chain's definition from `@reown/appkit/networks`.
// 4. If no primary RPC is found (neither via env var nor default), an error is logged, and a placeholder is used.
// This setup allows for flexible RPC configuration per environment and provides resilience through fallbacks.
export const chainRpcConfigs: Record<number, ChainRpcConfig> = {};

// Only configure RPC during runtime, not build time
if (!isBuildTime) {
  appChains.forEach(chain => {
  const chainName = chain.name.toLowerCase().replace(/\s/g, '');
  const primaryRpcEnvVar = `NEXT_PUBLIC_${chainName.toUpperCase()}_PRIMARY_RPC`;
  const fallbackRpcsEnvVar = `NEXT_PUBLIC_${chainName.toUpperCase()}_FALLBACK_RPCS`;

  // Direct environment variable access (Next.js will inline these at build time)
  let primaryRpcUrl: string | undefined;
  let fallbackRpcUrls: string[] = [];

  // Use direct variable access instead of dynamic lookup for better bundling
  switch (chain.id) {
    case 109: // Shibarium
      primaryRpcUrl = process.env.NEXT_PUBLIC_SHIBARIUM_PRIMARY_RPC;
      fallbackRpcUrls = process.env.NEXT_PUBLIC_SHIBARIUM_FALLBACK_RPCS?.split(',') || [];
      break;
    case 2000: // Dogechain
      primaryRpcUrl = process.env.NEXT_PUBLIC_DOGECHAIN_PRIMARY_RPC;
      fallbackRpcUrls = process.env.NEXT_PUBLIC_DOGECHAIN_FALLBACK_RPCS?.split(',') || [];
      break;
    case 8453: // Base
      primaryRpcUrl = process.env.NEXT_PUBLIC_BASE_PRIMARY_RPC;
      fallbackRpcUrls = process.env.NEXT_PUBLIC_BASE_FALLBACK_RPCS?.split(',') || [];
      break;
    case 1: // Mainnet
      primaryRpcUrl = process.env.NEXT_PUBLIC_MAINNET_PRIMARY_RPC;
      fallbackRpcUrls = process.env.NEXT_PUBLIC_MAINNET_FALLBACK_RPCS?.split(',') || [];
      break;
    default:
      // Fallback to dynamic lookup for any additional chains
      primaryRpcUrl = process.env[primaryRpcEnvVar];
      if (process.env[fallbackRpcsEnvVar]) {
        fallbackRpcUrls = process.env[fallbackRpcsEnvVar]?.split(',') || [];
      }
  }

  // Debug: Log the resolved RPC configuration
  console.log(`🔗 ${chain.name} (ID: ${chain.id}): Primary="${primaryRpcUrl}", Fallbacks=[${fallbackRpcUrls.join(', ')}]`);

  if (!primaryRpcUrl) {
    console.warn(`Warning: ${primaryRpcEnvVar} is not set. Using default public RPC for ${chain.name}.`);
    // Attempt to use a public RPC from the chain definition if available
    const httpTransport = chain.rpcUrls.public?.http[0] || chain.rpcUrls.default?.http[0];
    if (httpTransport) {
        primaryRpcUrl = httpTransport;
    } else {
        console.error(`Error: No default public RPC found for ${chain.name}. Please set ${primaryRpcEnvVar}.`);
        // Fallback to a generic placeholder if no public RPC is defined for the chain
        primaryRpcUrl = "http://localhost:8545"; // Placeholder, should be configured
    }
  }

  chainRpcConfigs[chain.id as number] = {
    chainId: chain.id as number,
    primaryRpcUrl,
    fallbackRpcUrls,
  };
  });
}

export const dappmetadata = {
  name: metaTitle,
  description: metaDescription,
  url: baseUrl, // origin must match your domain & subdomain
  icons: [`${baseUrl}/logo.${logoExt}`],
};


const connectors: CreateConnectorFn[] = [];

connectors.push(injected({ shimDisconnect: true }));
connectors.push(
  baseAccount({
    appName: dappmetadata.name,
    appLogoUrl: dappmetadata.icons[0],
  })
);
connectors.push(safe());
connectors.push(metaMask());

// Create minimal configs for build time
let wagmiAdapter: any;
let config: any;

// Provide minimal configuration during build time
if (isBuildTime) {
  // Create basic transports for build time
  const buildTransports: Record<number, any> = {};
  appChains.forEach(chain => {
    buildTransports[chain.id as number] = http('http://localhost:8545');
  });

  // Create a minimal WagmiAdapter for build time to prevent React context errors
  wagmiAdapter = new WagmiAdapter({
    storage: createStorage({
      storage: cookieStorage,
    }),
    connectors: [],
    batch: { multicall: { wait: 150 } },
    cacheTime: 50_000,
    transports: buildTransports,
    networks: appChains,
    ssr: true,
    projectId: projectId || 'build-time-placeholder',
  });
  
  config = wagmiAdapter.wagmiConfig;
}

if (!isBuildTime) {
  // Wagmi Transports Configuration with Rate Limiting:
  // This section configures the `transports` for the WagmiAdapter with global rate limiting.
  // Rate limiting: 5 burst max, 50 requests per second (shared across all chains)
  //
  // For each chain in `appChains`, it sets up a rate-limited fallback transport:
  // 1. Using the `primaryRpcUrl` from `chainRpcConfigs` with rate limiting applied.
  // 2. If the primary RPC fails, it attempts to connect using the URLs in `fallbackRpcUrls` in sequence.
  // 3. ALL RPC calls share the same rate limiter (5 token burst, 50 req/sec refill rate).
  // The `rank: false` option ensures that RPCs are tried in the order they are provided.
  const transports: Record<number, Transport> = {};
  appChains.forEach(chain => {
  const rpcConfig = chainRpcConfigs[chain.id as number];

  if (rpcConfig && rpcConfig.primaryRpcUrl) {
    // Create rate-limited transports for primary and fallback RPCs
    const rateLimitedTransports: Transport[] = [(
      createRateLimitedTransport(rpcConfig.primaryRpcUrl, {
        maxTokens: chainSettings[chain.id as ChainType].rpcSettings?.maxTokens ?? defaultRPCSettings.maxTokens,
        refillRate: chainSettings[chain.id as ChainType].rpcSettings?.refillRate ?? defaultRPCSettings.refillRate,
        batch: { batchSize: chainSettings[chain.id as ChainType].rpcSettings?.batchSize ?? defaultRPCSettings.batchSize, wait: chainSettings[chain.id as ChainType].rpcSettings?.batchWait ?? defaultRPCSettings.batchWait }
      })
  )];

    if (rpcConfig.fallbackRpcUrls && rpcConfig.fallbackRpcUrls.length > 0) {
      rpcConfig.fallbackRpcUrls.forEach((url: string) => {
        if (url) { // Ensure URL is not empty or undefined
          rateLimitedTransports.push(
            createRateLimitedTransport(url, {
              maxTokens: chainSettings[chain.id as ChainType].rpcSettings?.maxTokens ?? defaultRPCSettings.maxTokens,
              refillRate: chainSettings[chain.id as ChainType].rpcSettings?.refillRate ?? defaultRPCSettings.refillRate,
              batch: { batchSize: chainSettings[chain.id as ChainType].rpcSettings?.batchSize ?? defaultRPCSettings.batchSize, wait: chainSettings[chain.id as ChainType].rpcSettings?.batchWait ?? defaultRPCSettings.batchWait }
            })
          );
        }
      });
    }

    // Use fallback transport with rate-limited RPCs, try in order provided
    transports[chain.id as number] = fallback(rateLimitedTransports, { rank: false, retryDelay: chainSettings[chain.id as ChainType].rpcSettings?.retryDelay ?? defaultRPCSettings.retryDelay });

    console.log(`🚀 Rate-limited transport configured for ${chain.name} (${rateLimitedTransports.length} endpoints)`);
  } else {
    // Fallback to a default rate-limited transport if config is missing
    console.warn(
      `Warning: RPC configuration for chain ${chain.name} (ID: ${chain.id}) is missing or incomplete. ` +
      `Using default public RPC with rate limiting. Please check environment variables.`
    );
    // Attempt to use a public RPC from the chain definition if available
    const publicHttpTransport = chain.rpcUrls.public?.http[0] || chain.rpcUrls.default?.http[0];
    if (publicHttpTransport) {
        transports[chain.id as number] = createRateLimitedTransport(publicHttpTransport, {
          maxTokens: chainSettings[chain.id as ChainType].rpcSettings?.maxTokens ?? defaultRPCSettings.maxTokens,
          refillRate: chainSettings[chain.id as ChainType].rpcSettings?.refillRate ?? defaultRPCSettings.refillRate,
          batch: { batchSize: chainSettings[chain.id as ChainType].rpcSettings?.batchSize ?? defaultRPCSettings.batchSize, wait: chainSettings[chain.id as ChainType].rpcSettings?.batchWait ?? defaultRPCSettings.batchWait }
        })
    } else {
        console.error(`Error: No default public RPC found for ${chain.name} for Wagmi transport. Wagmi may not work correctly for this chain.`);
        // As a last resort, use a rate-limited placeholder
        transports[chain.id as number] = createRateLimitedTransport("http://localhost:8545/wagmi_placeholder_error");
    }
  }
  });

  wagmiAdapter = new WagmiAdapter({
  storage: createStorage({
    storage: cookieStorage,
  }),
  connectors,
  batch: { multicall: { wait: 150 } },
  cacheTime: 50_000,
  transports: transports,
  networks: appChains,
  ssr: true,
  projectId,
  });

  config = wagmiAdapter.wagmiConfig;

  // Rate limiting is now active!
  console.log(`
🎯 Global Rate Limiting Active:
   • 5 token burst maximum
   • 50 requests per second sustained
   • Shared across all ${appChains.length} chains
   • Look for console logs: "🌐 Global RPC request (X/5 tokens)"
  `);
}

export { wagmiAdapter, config };

/**
 * Rate Limiting Integration Complete!
 *
 * Every blockchain call in your app now respects the rate limits:
 * - useReadContract, useReadContracts, useWriteContract
 * - Custom Viem client calls
 * - NFT metadata fetching
 * - All marketplace interactions
 *
 * Monitor rate limiting in console:
 * 🌐 Global RPC request (3/5 tokens) - Normal operation
 * ⏳ Rate limit: waiting 100ms for next request - Throttling active
 * ⚠️ Rate limit hit, adding extra delay... - RPC returned rate limit error
 */
